import { Container } from '@amelio/ui-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import GenerateObjectiveAiModal from './components/form/GenerateObjectiveAiModal';
import KeepInMindTile from './components/form/KeepInMindTile';
import ObjectiveAssigneesCard from './components/form/ObjectiveAssigneesCard';
import ObjectiveDetailsCard from './components/form/ObjectiveDetailsCard';
import ObjectiveFormActions from './components/form/ObjectiveFormActions';
import ObjectiveMeasurementsSection from './components/form/ObjectiveMeasurementsSection';
import VisionAlignmentRow from './components/form/VisionAlignmentRow';
import { ObjectiveLoadingState, ObjectiveMessageState } from './components/ObjectivePageStates';
import { useFormErrorMessage } from './hooks/useFormErrorMessage';
import { useKeyResultResponsibles } from './hooks/useKeyResultResponsibles';
import { useObjectiveAiDraft } from './hooks/useObjectiveAiDraft';
import { useObjectiveForm } from './hooks/useObjectiveForm';
import { usePillarChoice } from './hooks/usePillarChoice';

import type { ObjectiveEmployeePickerProps } from './components/ObjectiveEmployeePicker';
import type { ObjectivesLocationState } from '@/navigation/objectivesRoutes';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';

import { useGetObjectiveById } from '@/api/objectives';
import { BackLink } from '@/components/BackLink';
import { OBJECTIVES_PAGE_LAYOUT } from '@/components/objectives/objectiveStyles';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog/UnsavedChangesDialog';
import { ObjectiveStatus } from '@/constants/ObjectiveStatus';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useObjectivesStepBack } from '@/hooks/objectives/useObjectivesStepBack';
import {
    readPeriodParam,
    useObjectiveFormPeriodOptions,
} from '@/hooks/objectives/usePeriodOptions';
import { useStrategicVision } from '@/hooks/objectives/useStrategicVision';
import { isResponseError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import {
    employeesWithoutObjectivesPath,
    newObjectivePath,
    objectiveDetailPath,
    objectivesPath,
    readEmployeeIdParam,
    stepBackState,
} from '@/navigation/objectivesRoutes';

interface ObjectiveFormRouteParams {
    type?: string;
    id?: string;
}

/** Retour steps back through the flow; Cancel leaves it for the screen it started from. */
type LeaveIntent = 'back' | 'cancel';

interface OwnerReach {
    hasCompanyWideReach: boolean;
    canEditOwnObjectives: boolean;
}

const toOwnerCandidates = ({
    hasCompanyWideReach,
    canEditOwnObjectives,
}: OwnerReach): ObjectiveEmployeePickerProps['ownerCandidates'] => {
    if (hasCompanyWideReach) {
        return 'company';
    }

    return canEditOwnObjectives ? 'reportingLineAndSelf' : 'reportingLine';
};

export default function ObjectiveFormPage() {
    const { t } = useTranslation();
    const translateError = useFormErrorMessage();
    const navigate = useNavigate();
    const { type: typeParam, id } = useParams<ObjectiveFormRouteParams>();
    const { search, state: locationState } = useLocation<ObjectivesLocationState | undefined>();
    const { canStepBack, stepBackOr } = useObjectivesStepBack();
    const fromTypeChoice = !!locationState?.fromTypeChoice;
    const forReader = locationState?.forReader;
    const isEditMode = !!id;
    // Coming from "Employees without an objective", which names the person the objective is for:
    // start with them already picked. In edit mode the objective's own employees win instead.
    const prefilledEmployeeId = isEditMode ? undefined : readEmployeeIdParam(search);
    // The period of the screen the creation began on, carried from screen to screen.
    const requestedPeriodId = readPeriodParam(search);

    const {
        hasCompanyWideReach,
        canManageOthersObjectives,
        canEditOwnObjectives,
        isLoading: areAbilitiesLoading,
        currentUserId,
    } = useObjectiveAbilities();
    // Someone who only manages their own objectives writes them for themselves: they own what they
    // create and cannot hand an objective over. They may still add participants.
    const writesOwnObjectivesOnly = !canManageOthersObjectives;
    // Begun in the reader's own portal, a new objective starts as theirs when they may hold one.
    const readerStartsAsHolder = writesOwnObjectivesOnly || (forReader && canEditOwnObjectives);

    const {
        data: existingObjective,
        isLoading: isLoadingObjective,
        error: objectiveError,
    } = useGetObjectiveById(id ?? '');
    const isNotFound =
        isEditMode && isResponseError(objectiveError) && objectiveError.response?.status === 404;
    const hasOtherError = isEditMode && !!objectiveError && !isNotFound;
    // A draft keeps offering "Save the draft" until it is published; a published objective never
    // goes back to draft, so editing one only offers to save the changes.
    const isEditingDraft = existingObjective?.status === ObjectiveStatus.DRAFT;
    const canSaveAsDraft = !isEditMode || isEditingDraft;
    // Without a screen to step back to, only a published objective has a page to return to; a
    // draft or a new objective returns to the list.
    const leavePath =
        id && !isEditingDraft
            ? objectiveDetailPath(id)
            : objectivesPath({ period: requestedPeriodId });

    // The form takes the place of the screen it is left for rather than stacking it again: stacked,
    // it would lead back here.
    const leaveForm = (intent: LeaveIntent) => {
        // Opened for one person of "Employees without an objective" with no screen to step back to
        // (a reload, a shared link): Retour still leads to that list, in the cycle it was showing,
        // not to a type choice the reader never went through.
        if (intent === 'back' && prefilledEmployeeId && !canStepBack) {
            navigate({
                path: employeesWithoutObjectivesPath({ period: requestedPeriodId }),
                replace: true,
            });
            return;
        }

        // A new objective's Retour reopens the type choice the form replaced, which still steps back
        // to the screen the creation started from. Opened from elsewhere ("Employees without an
        // objective"), it steps back there.
        const reopensTypeChoice =
            !isEditMode && intent === 'back' && (fromTypeChoice || !canStepBack);
        if (reopensTypeChoice) {
            navigate({
                path: newObjectivePath({ period: requestedPeriodId }),
                state: stepBackState({ canStepBack, forReader }),
                replace: true,
            });
            return;
        }

        stepBackOr({ path: leavePath, replace: true });
    };

    const pillarChoice = usePillarChoice(existingObjective?.pillarIds);
    const {
        options: periodOptions,
        defaultPeriodId,
        isLoading: isLoadingPeriods,
    } = useObjectiveFormPeriodOptions(existingObjective?.objectivePeriod);
    // Begun from a screen filtered on a period (the objectives page, the portal, "Employees without an
    // objective"), a new objective starts in that period when it may be attached to it.
    const initialPeriodId = periodOptions.some((option) => option.id === requestedPeriodId)
        ? requestedPeriodId
        : defaultPeriodId;
    const strategicVision = useStrategicVision();

    const form = useObjectiveForm({
        objectiveId: id,
        existingObjective,
        prefilledEmployeeId,
        pillarChoice,
        periodOptions,
        defaultPeriodId: initialPeriodId,
        selfOwnerId: !areAbilitiesLoading && readerStartsAsHolder ? currentUserId : undefined,
    });

    const employeeIds = form.watch('employeeIds');
    const participantAttributes = form.watch('participantAttributes');
    // The first employee owns the objective; a second employee or an attribute makes it a team one.
    const isTeam = employeeIds.length > 1 || participantAttributes.length > 0;
    // Chosen as a team objective, or become one: the form takes several people and gives team advice.
    const isTeamForm = typeParam === 'team' || isTeam;
    const ownerId = employeeIds[0] as string | undefined;

    const employeeOptions = useKeyResultResponsibles({
        objectiveId: id,
        employeeIds,
        participantAttributes,
        canResolveAttributes: canManageOthersObjectives || isEditMode,
        savedResponsibles: form.savedResponsibles,
        getValues: form.getValues,
        setValue: form.setValue,
    });

    const aiDraft = useObjectiveAiDraft({
        getValues: form.getValues,
        setValue: form.setValue,
        onDescriptionReplaced: form.reseedDescription,
    });

    const titleCardRef = useRef<HTMLDivElement>(null);

    // How the reader asked to leave while the form held changes, until they confirm or stay.
    const [pendingLeave, setPendingLeave] = useState<LeaveIntent | null>(null);

    const requestLeave = (intent: LeaveIntent) => {
        if (form.isDirty) {
            setPendingLeave(intent);
            return;
        }

        leaveForm(intent);
    };

    const handleRequestTitle = () => {
        titleCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // The title `Controller` registers no ref, so react-hook-form's `setFocus('title')` would do
        // nothing. `preventScroll` keeps the focus from cancelling the smooth scroll just started.
        titleCardRef.current
            ?.querySelector<HTMLInputElement>('[data-testid="objective-form-title-input"]')
            ?.focus({ preventScroll: true });
    };

    const handleAttributesChange = (nextAttributes: ObjectiveParticipantAttribute[]) => {
        form.setValue('participantAttributes', nextAttributes, { shouldDirty: true });
        // The holder rule reports on `employeeIds`, and `participantAttributes` is never
        // registered, so its change alone never re-runs the check that field carries.
        void form.trigger('employeeIds');
    };

    // A failed load never hydrates the form, so it is shown here rather than behind the loader.
    if (isNotFound || hasOtherError) {
        return (
            <ObjectiveMessageState
                message={t(isNotFound ? 'objectives.detail.notFound' : 'objectives.errors.loading')}
                isError={!isNotFound}
                onBack={() => stepBackOr({ path: objectivesPath() })}
                backLabel={t('objectives.detail.back')}
                backDataTestId={'objective-form-back'}
            />
        );
    }

    // A holder who may not change the objective has nothing to write here: they read it on its page.
    if (existingObjective && !existingObjective.canEdit) {
        return (
            <Redirect
                to={{ pathname: objectiveDetailPath(existingObjective.id), state: locationState }}
            />
        );
    }

    if (isEditMode && (isLoadingObjective || !form.isHydrated)) {
        return <ObjectiveLoadingState dataTestId={'objective-form-loader'} />;
    }

    return (
        <Container
            className={OBJECTIVES_PAGE_LAYOUT.shell}
            dataTestId={'objective-form-page'}
        >
            <form
                ref={form.formRef}
                onSubmit={form.handleFormSubmit}
                className={'pt-6 pb-8'}
            >
                <BackLink
                    onClick={() => requestLeave('back')}
                    label={t('objectives.detail.back')}
                    dataTestId={'objective-form-back'}
                    className={'mt-4 mb-6'}
                />

                <div className={'flex flex-col gap-[48px]'}>
                    <KeepInMindTile writesOwnObjectivesOnly={writesOwnObjectivesOnly} />

                    <VisionAlignmentRow
                        strategicVision={strategicVision}
                        pillarChoice={pillarChoice}
                        control={form.control}
                    />

                    <ObjectiveAssigneesCard
                        control={form.control}
                        isTeam={isTeamForm}
                        employeePicker={{
                            mode: isTeamForm ? 'multiple' : 'simple',
                            ownerCandidates: toOwnerCandidates({
                                hasCompanyWideReach,
                                canEditOwnObjectives,
                            }),
                            isOwnerLocked: writesOwnObjectivesOnly,
                            selectedAttributes: participantAttributes,
                            onAttributesChange: handleAttributesChange,
                            canPickAttributes: canManageOthersObjectives,
                            objectiveId: id,
                        }}
                        period={{
                            options: periodOptions,
                            selectedPeriodId: form.selectedPeriodId,
                            onPeriodChange: form.handlePeriodChange,
                            dateErrorMessages: {
                                startDate: translateError(form.errors.startDate?.message),
                                endDate: translateError(form.errors.endDate?.message),
                            },
                            isLoadingPeriods,
                        }}
                    />

                    <ObjectiveDetailsCard
                        control={form.control}
                        titleCardRef={titleCardRef}
                        descriptionEditor={form.descriptionEditor}
                        aiAssistant={
                            canManageOthersObjectives
                                ? { isEnabled: aiDraft.isEnabled, onOpen: aiDraft.open }
                                : undefined
                        }
                        isTeam={isTeam}
                        ownerId={ownerId}
                    />

                    <ObjectiveMeasurementsSection
                        control={form.control}
                        setValue={form.setValue}
                        employeeOptions={employeeOptions}
                        isTeam={isTeam}
                        onRequestTitle={handleRequestTitle}
                    />

                    <ObjectiveFormActions
                        canSaveAsDraft={canSaveAsDraft}
                        isSaving={form.isSaving}
                        savingStatus={form.savingStatus}
                        onChooseStatus={form.chooseSubmitStatus}
                        onCancel={() => requestLeave('cancel')}
                    />
                </div>
            </form>

            <GenerateObjectiveAiModal
                isOpen={aiDraft.modal.isOpen}
                onClose={aiDraft.close}
                onGenerate={aiDraft.generateDraft}
                onApply={aiDraft.applyDraft}
                hasKeyResults={aiDraft.modal.hasKeyResults}
            />

            <UnsavedChangesDialog
                isOpen={pendingLeave !== null}
                onConfirm={() => pendingLeave && leaveForm(pendingLeave)}
                onCancel={() => setPendingLeave(null)}
            />
        </Container>
    );
}
